'use client'

import {
  MobileNavigationMenuCategoryItem,
  MobileNavigationMenuCategoryList,
  MobileNavigationMenu as MobileNavigationMenuRoot,
  MobileNavigationMenuSubCategoryItem,
  MobileNavigationMenuSubCategoryItemLabel,
  MobileNavigationMenuSubCategoryItemLink,
  MobileNavigationMenuSubCategoryItemWrapper,
  MobileNavigationMenuSubCategoryList,
} from '@dafiti/closet-ui'
import { cn } from '@dafiti/closet-ui/utils'
import { FocusTrap } from 'focus-trap-react'
import React, { useEffect } from 'react'

import type { WithCustomLink } from '@/components/DefaultLink'
import type { MenuItemData } from '@/queries/header'
import { useIsClient } from '@/hooks/useIsClient'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { useMessages } from '@/i18n/useMessages'
import { useMenuStore } from '@/store/menu.slice'
import * as track from '@/tracking/header'
import MobileNavigationMenuState from '@/components/PageHeader/Menu/MobileNavigationMenuState'
import { MobileNavigationUserMenu } from '@/components/PageHeader/Menu/MobileNavigationUserMenu'
import { getActiveCategoryLabel } from '@/components/PageHeader/Menu/getActiveCategoryLabel'

const classnames = {
  trigger: cn('pr-6 md:pr-10'),
  label: cn('container min-w-auto -ml-3'),
}

type MobileNavigationMenuProps = {
  data: Array<MenuItemData>
} & Partial<React.ComponentProps<typeof MobileNavigationUserMenu>>

const RecursiveComponent = ({
  category,
  path,
  LinkComponent,
}: WithCustomLink<{
  category: MenuItemData
  path: Array<string>
}>) => {
  const t = useMessages()

  // Track the click on the subcategory
  function handleMenuSubCategoryClick(additionalLabel?: string) {
    const segments = [...path, additionalLabel].filter((_path) => !!_path)

    track.menuSubCategoryClick({
      complement: segments.join(' - '),
    })
  }

  // Avoid rendering item if type is button and url is not defined
  if (category.renderItemsAs === 'button' && !category.url) {
    return null
  }

  if (category.items?.length && category.renderItemsAs !== 'button') {
    // Renders the subcategories from current category
    const SubCategories = (
      <>
        {/* Adds the "See all" link if category.seeAll exists*/}
        {category.seeAll && (
          <MobileNavigationMenuSubCategoryItemLink
            as={LinkComponent}
            href={category.seeAll}
            className={classnames.trigger}
            onClick={() => handleMenuSubCategoryClick('Ver tudo')}
          >
            <span className={classnames.label}>{t.menu.seeAll}</span>
          </MobileNavigationMenuSubCategoryItemLink>
        )}

        {category.items.map((subCategory) => (
          <RecursiveComponent
            category={subCategory}
            path={[...path, subCategory.label || '']}
            key={subCategory.label}
            LinkComponent={LinkComponent}
          />
        ))}
      </>
    )

    // Avoid adding a new accordion if the category has no label or url
    if (!category.label && !category.url) {
      return SubCategories
    }

    // If the category has a label and url, render it as a new accordion
    return (
      <MobileNavigationMenuSubCategoryItem
        className="overflow-hidden"
        value={category.label || ''}
      >
        <MobileNavigationMenuSubCategoryItemLabel
          className={classnames.trigger}
        >
          <span className={classnames.label}>{category.label}</span>
        </MobileNavigationMenuSubCategoryItemLabel>

        <MobileNavigationMenuSubCategoryItemWrapper>
          {SubCategories}
        </MobileNavigationMenuSubCategoryItemWrapper>
      </MobileNavigationMenuSubCategoryItem>
    )
  }

  // Simple link if the category has no subcategories
  return (
    <MobileNavigationMenuSubCategoryItemLink
      as={LinkComponent}
      href={category.url}
      className={classnames.trigger}
      onClick={() => handleMenuSubCategoryClick()}
    >
      <span className={classnames.label}>{category.label}</span>
    </MobileNavigationMenuSubCategoryItemLink>
  )
}

export const MobileNavigationMenu = ({ data }: MobileNavigationMenuProps) => {
  const { LinkComponent, router } = useAppLayoutContext()
  const t = useMessages()

  const isMenuOpen = useMenuStore((menu) => menu.isOpen)
  const closeMenu = useMenuStore((menu) => menu.close)
  const isHydrated = useIsClient()

  const activeSegmentLabel = getActiveCategoryLabel(data, router?.pathname)

  // Close the menu on navigation. The header persists across route changes
  // (App Router layout), so a link click alone never unmounts it.
  useEffect(() => {
    closeMenu()
  }, [router?.pathname, closeMenu])

  function getTabListComponent(category: MenuItemData) {
    if (category.items?.length && isHydrated) {
      return <button>{category.label}</button>
    }

    return (
      <LinkComponent href={category.url as string}>
        {category.label as string}
      </LinkComponent>
    )
  }

  return (
    <>
      <MobileNavigationMenuState />

      <FocusTrap
        active={isMenuOpen}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          escapeDeactivates: true,
        }}
      >
        <nav
          className="h-0 overflow-hidden transition-all duration-300 peer-checked:h-auto"
          style={{
            interpolateSize: 'allow-keywords',
          }}
          data-testid="mobile-menu"
        >
          <MobileNavigationUserMenu />

          <MobileNavigationMenuRoot
            key={isMenuOpen ? 'menu-open' : 'menu-closed'}
            defaultValue={activeSegmentLabel}
          >
            <MobileNavigationMenuCategoryList
              className="container border-b-gray-200"
              aria-label={t.menu.categoriesAriaLabel}
            >
              {data.map((category) => (
                <MobileNavigationMenuCategoryItem
                  className="first:-ml-3.5 active:bg-gray-200"
                  value={category.label as string}
                  key={category.label}
                  onClick={() =>
                    track.menuCategoryClick({
                      complement: category.label as string,
                    })
                  }
                  asChild
                >
                  {getTabListComponent(category)}
                </MobileNavigationMenuCategoryItem>
              ))}
            </MobileNavigationMenuCategoryList>

            {data.map(
              (category) =>
                !!category.items?.length && (
                  <MobileNavigationMenuSubCategoryList
                    value={category.label || ''}
                    key={category.label}
                  >
                    {/* Adds the "See all" link if category.seeAll exists */}
                    {category.seeAll && (
                      <MobileNavigationMenuSubCategoryItemLink
                        as={LinkComponent}
                        href={category.seeAll}
                        className={classnames.trigger}
                        onClick={() =>
                          track.menuSubCategoryClick({
                            complement: `${category.label} - Ver tudo`,
                          })
                        }
                      >
                        <span className={cn(classnames.label, 'font-medium')}>
                          {t.menu.seeAll}
                        </span>
                      </MobileNavigationMenuSubCategoryItemLink>
                    )}

                    {category.items.map((subCategory, index) => (
                      <RecursiveComponent
                        category={subCategory}
                        path={[
                          category.label as string,
                          subCategory.label || '',
                        ]}
                        key={subCategory.label || index}
                        LinkComponent={LinkComponent}
                      />
                    ))}
                  </MobileNavigationMenuSubCategoryList>
                ),
            )}
          </MobileNavigationMenuRoot>
        </nav>
      </FocusTrap>
    </>
  )
}
